import { format } from 'date-fns'
import { computed, makeObservable, observable } from 'mobx'
import { EMPTY_NUMBER_ID } from 'src/constant/common'

import { DTO } from '../DTO'

export class Worksheet extends DTO {
  id: res.Worksheet['id'] = EMPTY_NUMBER_ID
  schoolType: res.Worksheet['schoolType'] = 'ELEMENTARY'
  grade: res.Worksheet['grade'] = '1'
  revision: res.Worksheet['revision'] = 'CURRICULUM_15'
  title: res.Worksheet['title'] = ''
  titleTag: res.Worksheet['titleTag'] = ''
  tag?: res.Worksheet['tag']
  chapter: res.Worksheet['chapter'] = ''
  createDatetime?: Date
  problemCount: res.Worksheet['problemCount'] = 0
  accessModifierToStudent: res.Worksheet['accessModifierToStudent'] = 'PUBLIC'
  baseWorkbookType: res.Worksheet['baseWorkbookType'] = null
  writer?: res.MyWorksheet['writer'] | res.PracticeTestWorksheet['writer']
  level?:
    | res.MyWorksheet['level']
    | res.RecommendWorksheet['level']
    | res.ChapterTestWorksheet['level']
    | res.EntranceTestWorksheet['level']
    | res.WeeklyTestWorksheet['level']
  autoScorable?: res.MyWorksheet['autoScorable']
  rate?: res.RecommendWorksheet['rate']
  assignList?:
    | res.MyWorksheet['assignList']
    | res.RecommendWorksheet['assignList']
    | res.ChapterTestWorksheet['assignList']
    | res.EntranceTestWorksheet['assignList']
    | res.WeeklyTestWorksheet['assignList']
    | res.ArithmeticWorksheet['assignList']
  type?: res.ArithmeticWorksheet['type']
  thumbnail?: res.ArithmeticWorksheet['thumbnail']
  publisher?: res.TextbookWorksheet['publisher']
  filterType?: res.Worksheet['filterType']
  universityType?: res.PoliceMilitaryPracticeWorksheet['universityType']

  constructor(data) {
    super()

    if (data) {
      Object.assign(this, data)
    }

    if (data.createDatetime) {
      this.createDatetime = new Date(data.createDatetime)
    }

    makeObservable(this, {
      id: observable,
      schoolType: observable,
      grade: observable,
      title: observable,
      titleTag: observable,
      tag: observable,
      chapter: observable,
      createDatetime: observable,
      problemCount: observable,
      accessModifierToStudent: observable,
      writer: observable,
      level: observable,
      autoScorable: observable,
      rate: observable,
      assignList: observable,
      formatDate: computed,
      koreanLevel: computed,
      koSchoolType: computed,
      subjectGrade: computed,
      koLabel: computed,
      assignedStudentIdList: computed,
      assignedCount: computed,
      assignedStudentWorksheetList: computed,
      publisher: observable,
      baseWorkbookType: observable,
      universityType: observable,
    })
  }

  get formatDate() {
    if (!this.createDatetime) return null
    return format(this.createDatetime, 'yy.MM.dd')
  }

  get koreanLevel() {
    switch (this.level) {
      case 1:
        return '하'
      case 2:
        return '중하'
      case 3:
        return '중'
      case 4:
        return '상'
      case 5:
        return '최상'
      default:
        return '중'
    }
  }

  get koSchoolType() {
    switch (this.schoolType) {
      case 'ELEMENTARY':
        return '초등'
      case 'MIDDLE':
        return '중등'
      case 'HIGH':
        return '고등'
    }
  }

  // 내 학습지, 테마별 내신대비추천은 학년+과목을 사용한다.
  get subjectGrade() {
    if (this.schoolType === 'HIGH') {
      return this.grade
    }
    return this.grade + '학년'
  }

  // 테마별 학습지는 초1, 중2, 고3 과 같은 형식 사용한다.
  get koLabel() {
    let label = ''
    switch (this.schoolType) {
      case 'ELEMENTARY':
        label += '초'
        break
      case 'MIDDLE':
        label += '중'
        break
      case 'HIGH':
        label += '고'
    }
    return label + this.grade
  }

  get assignedStudentWorksheetList() {
    return this.assignList
      ?.filter(({ studentStatus }) => studentStatus === 'ACTIVE')
      .map(({ studentId, studentWorksheetId, studentName }) => ({
        studentId,
        studentDataRelationId: studentWorksheetId,
        studentName,
      }))
  }

  get assignedStudentIdList() {
    return this.assignList?.map(({ studentId }) => studentId)
  }

  get assignedCount() {
    return this.assignList?.length
  }

  static subTypeKr(
    subType: keyof common.WorksheetSubTypeEnum,
    options: {
      availableSubTypes: (keyof common.WorksheetSubTypeEnum)[]
      fallbackText?: string | null
    } = { availableSubTypes: [], fallbackText: null },
  ) {
    if (!options.availableSubTypes.includes(subType)) return options.fallbackText

    switch (subType) {
      case 12:
      case 16:
        return '챌린지'
      case 13:
        return '자기주도학습'
      case 14:
        return '오답보충학습'
      case 15:
        return '심화보충학습'
    }

    // 지원하는 케이스가 생길 때 추가
    return null
  }
}
